import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";
import { gsap, Elastic } from "gsap";

import {
  appendNewMessage,
  updateUnreadMessageCount,
  fetchChatRoomMessages,
  clearUnreadMessageCount,
  selectRoom,
  loadMoreMessages,
  deleteMessage,
  blockedUser,
  unBlockedUser,
} from "../../../../store/slices/chat";
import ChatAreaHeader from "./ChatAreaHeader";
import ChattingText from "./ChattingText";
import TextareField from "./TextareField";
import "../../../../assets/css/chat/chattingArea.css";
import "../../../../assets/css/chat/chating-text-area.css";
import useHttp from "../../../../hooks/useHttp";
import { ROOM_MESSAGES_GET_URL } from "../../../../utils/api-ends";
import LoadingGif from "../../../../assets/media/loader.gif";
import { updateTotalRoomUnreadMessageCount } from "../../../../store/slices/basicInfo";

const access_token = JSON.parse(localStorage.getItem("auth_token"))?.access;

const ChattingArea = () => {
  let chatBoxRef = useRef();
  const dispatch = useDispatch();
  const { channelId } = useParams();
  const { sendRequests: fetchMessages } = useHttp();
  const { send_message_ui } = useSelector((state) => state.chat_ui);

  const chatSocket = useSelector((state) => state.chat.socket);
  const selectedRoom = useSelector((state) => state.chat.selectedRoom);
  const chatRooms = useSelector((state) => state.chat.chatRooms.results);
  const selectedChannel = chatRooms.find(
    (room) => room.channel_id === channelId
  );
  let ajaxRequest = null;
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Auto select room if there is value in the params
  useEffect(() => {
    if (![null, undefined].includes(selectedChannel)) {
      dispatch(selectRoom(selectedChannel));

      // Join room
      chatSocket?.send(
        JSON.stringify({
          command: "join",
          room_id: selectedChannel.channel_id,
        })
      );
    }
  }, [![null, undefined].includes(selectedChannel)]);

  // Gets the messages for first load
  useEffect(() => {
    if (channelId) {
      const response = (data) =>
        dispatch(fetchChatRoomMessages({ data: data, channelId: channelId }));

      fetchMessages(
        {
          url: ROOM_MESSAGES_GET_URL,
          method: "GET",
          params: {
            room_id: channelId,
          },
        },
        response
      );
    }
  }, [channelId]);

  // Socket response
  useEffect(() => {
    if (chatSocket) {
      chatSocket.onmessage = (event) => {
        const data = JSON.parse(event.data);

        // New message arrived
        if (data.response_type === "new_message") {
          dispatch(
            appendNewMessage({
              roomInfo: data.room_info,
              message: data.message,
            })
          );
        }

        // Unread message count
        if (data.response_type === "unread_message") {
          dispatch(
            updateUnreadMessageCount({
              roomInfo: data.chat_room_info,
              unreadMessageCount: data.room_unread_message_count,
            })
          );

          // If the room is selected, clear the unread message count
          if (
            data.chat_room_info.channel_id === selectedRoom?.channel_id &&
            chatBoxRef.current.scrollTop === 0
          ) {
            // Read message
            chatSocket?.send(
              JSON.stringify({
                command: "message_read",
                room_id: data.chat_room_info.channel_id,
              })
            );
          }
        }

        // Read unread messages
        if (data.response_type === "message_read") {
          dispatch(clearUnreadMessageCount(data.channel_id));

          dispatch(
            updateTotalRoomUnreadMessageCount(data.total_unread_message_rooms)
          );
        }

        // Message deleted
        if (data.response_type === "message_deleted") {
          dispatch(deleteMessage({ messageId: data.message_id }));
        }

        // Block user
        if (data.response_type === "blocked_user") {
          dispatch(blockedUser(data));
        }
        // Unblock user
        if (data.response_type === "unblocked_user") {
          dispatch(unBlockedUser(data));
        }
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSocket, selectedRoom, chatRooms]);

  useEffect(() => {
    const handleScroll = () => {
      // Read message when the user scrolls to the bottom of the chat box
      if (
        chatBoxRef.current.scrollTop === 0 &&
        selectedRoom.unread_message_count > 0
      ) {
        chatSocket?.send(
          JSON.stringify({
            command: "message_read",
            room_id: selectedRoom.channel_id,
          })
        );
      }

      // Free scrolling and load more messages
      const totalValue = selectedRoom.messages?.pagination?.total_value; // How many value is got from the previous request
      if (
        Math.abs(chatBoxRef.current.scrollTop) + 10 >=
          chatBoxRef.current.scrollHeight - chatBoxRef.current.offsetHeight &&
        totalValue !== 0
      ) {
        setIsLoadingMore(true); // start loading

        // Cancel  previous ajax if exists
        if (ajaxRequest) {
          ajaxRequest.cancel();
        }

        // creates a new token for upcomming ajax (overwrite the previous one)
        ajaxRequest = axios.CancelToken.source();
        const params = {
          room_id: channelId,
        };

        // Avoid those messages are already loaded in the chat box without paginating them
        const lastMsg =
          selectedRoom.messages?.results[
            selectedRoom.messages?.results.length - 1
          ];
        if (lastMsg && lastMsg.id) {
          params["last_msg_id"] = lastMsg.id;
        }

        axios({
          url: ROOM_MESSAGES_GET_URL.url,
          method: "get",
          params: params,
          cancelToken: ajaxRequest.token,
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        }).then((response) => {
          dispatch(
            loadMoreMessages({ data: response.data, channelId: channelId })
          );
          setIsLoadingMore(false); // start loading
        });
      }
    };
    chatBoxRef?.current?.addEventListener("scroll", handleScroll);
    return () => {
      chatBoxRef?.current?.removeEventListener("scroll", handleScroll);
      setIsLoadingMore(false);
    };
  }, [chatBoxRef, selectedRoom.messages]);

  useEffect(() => {
    var last_user_text =
      chatBoxRef?.current.querySelectorAll(".single-text-field")[0];

    if (send_message_ui && last_user_text) {
      gsap.fromTo(
        last_user_text,
        {
          scale: 0.1,
        },
        {
          scale: 1,
          ease: Elastic.easeOut.config(1, 0.3),
        }
      );
    }
  }, [chatBoxRef, selectedRoom.messages, send_message_ui]);

  console.log(selectedRoom)

  return (
    <div className="chattingArea">
      <ChatAreaHeader
        orderTrackingId={selectedRoom?.order_details?.tracking_id}
        username={selectedRoom.partner_info?.full_name}
        profileImage={selectedRoom.partner_info?.profile_image}
      />

      <div className="chatting-text-area">
        <div className="chatting-text-wrapper" ref={chatBoxRef}>
          {selectedRoom.messages?.results?.map((item, index, { length }) => {
            return (
              <ChattingText
                key={item.id}
                sendBy={item.send_by}
                voice={item.voice}
                messageText={item.message_text}
                messageType={item.message_type}
                profileImage={item.sender?.profile_pic}
                createdDate={item.created_date_obj}
                attachmentLinks={item.attachment_links}
                messageId={item.id}
                roomId={channelId}
                isDeleted={item.is_deleted}
                last_text={index === 0 ? "true" : "false"}
                length={length}
              />
            );
          })}

          {selectedRoom.messages?.results?.length === 0 && (
            <p style={{ textAlign: "center", color: "grey" }}>No messages</p>
          )}

          {isLoadingMore && (
            <img
              src={LoadingGif}
              alt="loading"
              className="loading-gif"
              style={{ width: "25px", alignSelf: "center", marginTop: "10px" }}
            />
          )}
        </div>
      </div>

      {selectedRoom?.is_blocked === false &&
        selectedRoom?.can_send_message === true && <TextareField />}
    </div>
  );
};

export default ChattingArea;
